import React, { useEffect, useState } from 'react';
import { Form, Input, Radio, Select, Button, Row, Col, Card, message, Upload } from 'antd';
import { PlusCircleFilled, CameraOutlined } from '@ant-design/icons';
import { CustomerModel } from '@app/interfaces/customer';
import InputMask from 'react-input-mask';
import { useAppSelector, useAppDispatch } from '@app/hooks/reduxHooks';
import { RootState } from '@app/store/store';
import { createCustomerGroup, getCustomerGroups } from '@app/store/slices/customerGroupSlice';
import { getDistrictsApi, getProvincesApi, getWardsApi } from '@app/api/province.api';
import CustomerGroupForm from '../CustomerGroupForm';
import ImagePreviewAvatar from '@app/components/common/ImagePreviewAvatar';
import { updateCustomer } from '@app/store/slices/customerSlice';
import { getFullBlobUrl } from '@app/utils/blobUrl';
import { DATE_FORMAT } from '@app/constants/dateFormats';

const OverviewForm = ({ record, onClose }: { record: CustomerModel, onClose: () => void }) => {
  const [form] = Form.useForm();
  const [provinceOptions, setProvinceOptions] = useState<any>([]);
  const [districtOptions, setDistrictOptions] = useState<any>([]);
  const [wardOptions, setWardOptions] = useState<any>([]);
  const [provinceCode, setProvinceCode] = useState<string>('');
  const [showCustomerGroupForm, setShowCustomerGroupForm] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string>('');
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);

  const { data: customerGroups = [] } = useAppSelector((state: RootState) => state.customer_group || {});
  const dispatch = useAppDispatch();

  useEffect(() => {
    console.log(record)
    if (record) {
      form.setFieldsValue(record);
      if (record.avatar_url) {
        setAvatarPreview(getFullBlobUrl(record.avatar_url) || '');
      }

      const customer_group_id = record.customer_group?.id;
      if (customer_group_id) {
        form.setFieldsValue({ customer_group_ids: [customer_group_id] });
      }

      if (record.province_code) {
        fetchDistricts(record.province_code);
        setProvinceCode(record.province_code);
      }

      // If we have both province_code and district_code, load wards
      if (record.province_code && record.district_code) {
        fetchWards(record.province_code, record.district_code);
      }
    }
  }, [record]);

  useEffect(() => {
    fetchCustomerGroup();
    fetchProvinces();
  }, []);

  const fetchCustomerGroup = async () => {
    await dispatch(getCustomerGroups());
  };

  const fetchProvinces = async () => {
    const repsonse = await getProvincesApi();
    if (repsonse) {
      const options = repsonse && repsonse.map((item: any) => ({value: item.province_code, label: item.name}))
      setProvinceOptions(options)
    }
  }

  const fetchDistricts = async (provinceCode: string) => {
    if (!provinceCode) return;
    setProvinceCode(provinceCode);

    const repsonse = await getDistrictsApi(provinceCode);
    if (repsonse) {
      const options = repsonse && repsonse.map((item: any) => ({value: item.district_code, label: item.name}))
      setDistrictOptions(options)
    }
  }

  const fetchWards = async (provinceCode: string, districtCode: string) => {
    if (!provinceCode && !districtCode) return;

    const repsonse = await getWardsApi(provinceCode, districtCode);
    if (repsonse) {
      const options = repsonse && repsonse.map((item: any) => ({value: item.ward_code, label: item.name}))
      setWardOptions(options)
    }
  }

  const customerGroupOptions = customerGroups?.map((item) => ({ value: item.id, label: item.name }));

  const handleSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        onCreate(values);
      })
      .catch((info) => {
        console.log('Validate failed:', info);
      });
  };

  const handleAvatarUpload = (file: File) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('Vui lòng tải lên một tập tin hình ảnh!');
      return false;
    }

    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error('Hình ảnh phải nhỏ hơn 2MB!');
      return false;
    }

    setAvatarFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
    return false;
  };

  const handleRemoveAvatar = () => {
    setAvatarFile(null);
    setAvatarPreview('');
  };

  const onCreate = async (values: any) => {
    try {
      const formData = new FormData();

      // Append all form values
      Object.keys(values).forEach((key) => {
        if (values[key] !== undefined && values[key] !== null) {
          if (Array.isArray(values[key])) {
            values[key].forEach((item: any, index: number) => {
              formData.append(`${key}[${index}]`, item);
            });
          } else {
            formData.append(key, values[key]);
          }
        }
      });

      // Append avatar file if exists
      if (avatarFile) {
        formData.append('avatar', avatarFile);
      }

      setIsUploadingAvatar(true);
      const response = await dispatch(updateCustomer({customerId: record.id, formData}));

      // Update avatar preview from response if avatar was uploaded
      if ((response.payload as any)?.data?.avatar_url) {
        setAvatarPreview(getFullBlobUrl((response.payload as any)?.data?.avatar_url) || '');
      }

      // Clear avatar file after successful upload
      setAvatarFile(null);

      message.success('Cập nhật khách hàng thành công!');
      onClose();
    } catch (error: any) {
      if (error instanceof Error) {
        message.error(error.message);
      } else {
        message.error('An unknown error occurred');
      }
    } finally {
      setIsUploadingAvatar(false);
    }
  }

  const onChangeCustomerGroup = (value: string[]) => {
    form.setFieldsValue({ customer_group_ids: value });
  };

  const handleAddCustomerGroup = () => {
    setShowCustomerGroupForm(true);
  };

  const onCreateCustomerGroup = (values: any) => {
    try {
      dispatch(createCustomerGroup(values));
      setShowCustomerGroupForm(false);
    }
    catch(error: any) {
      console.log(error.message);
    }
  }

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        name="form-customer"
        initialValues={{
          customer_type: 'individual',
          store_id: 1,
          gender: false,
        }}
        size="small"
        autoComplete="off" 
      >
        <Row>
          <Col span={12}>
            <Card style={{marginLeft: 10, marginRight: 10}}>
              <Row gutter={12}>
                <Col span={7}></Col>
                <Col span={17}>
                  <Form.Item name="customer_type">
                    <Radio.Group>
                      <Radio value="individual">Cá nhân</Radio>
                      <Radio value="business">Doanh nghiệp</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={12}>
                <Col span={7}>Tên khách hàng</Col>
                <Col span={17}>
                  <Form.Item
                    name="name"
                    rules={[{ required: true, message: 'Please input customer name!' }]}
                  >
                    <Input placeholder="Tên khách hàng" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={12}>
                <Col span={7}>Số điện thoại</Col>
                <Col span={17}>
                  <Form.Item
                    name="phone"
                  >
                    <Input placeholder="Số điện thoại" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={12}>
                <Col span={7}>Email</Col>
                <Col span={17}>
                  <Form.Item
                    name="email"
                    rules={[
                      {
                        type: 'email',
                        message: 'The input is not valid email!',
                      },
                      // {
                      //   required: true,
                      //   message: 'Please input your email!',
                      // },
                    ]}
                  >
                    <Input placeholder="Email" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={12}>
                <Col span={7}>Ngày sinh</Col>
                <Col span={17}>
                  <Form.Item
                    name="date_of_birth"
                    rules={[
                      //{ required: true, message: 'Please enter your date of birth!' },
                      {
                        pattern: /^\d{2}\-\d{2}\-\d{4}$/,
                        message: 'Please enter a valid date in the format DD-MM-YYYY',
                      },
                    ]}
                  >
                    <InputMask mask="99-99-9999" placeholder={DATE_FORMAT}>
                      {(inputProps: any) => <Input {...inputProps} />}
                    </InputMask>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={12}>
                <Col span={7}>Phái</Col>
                <Col span={17}>
                  <Form.Item name="gender">
                    <Radio.Group>
                      <Radio value={false}>Nam</Radio>
                      <Radio value={true}>Nữ</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>
          <Col span={12}>
            <Card style={{marginLeft: 10, marginRight: 10}}>
              {/* Avatar Upload Section */}
              <Row gutter={12} style={{ marginBottom: 16 }}>
                <Col span={24}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ marginBottom: 12 }}>
                      <ImagePreviewAvatar
                        src={avatarPreview}
                        size={80}
                        onRemove={handleRemoveAvatar}
                        showRemoveButton={!!avatarPreview}
                      />
                    </div>
                    <Upload
                      maxCount={1}
                      beforeUpload={handleAvatarUpload}
                      showUploadList={false}
                      accept="image/*"
                    >
                      <Button 
                        type="primary" 
                        size="small" 
                        loading={isUploadingAvatar}
                        icon={<CameraOutlined />}
                      >
                        Tải lên ảnh đại diện
                      </Button>
                    </Upload>
                  </div>
                </Col>
              </Row>

              <Row gutter={12}>
                <Col span={7}>Nhóm khách hàng</Col>
                <Col span={17}>
                  <Form.Item
                    name="customer_group_ids"
                    rules={[{ required: true, message: 'Please select a customer group!' }]}
                  >
                    <Select
                      allowClear
                      showSearch
                      placeholder="Chọn nhóm khách hàng"
                      mode="multiple"
                      optionFilterProp="children"
                      onChange={onChangeCustomerGroup}
                      filterOption={(input, option) =>
                        (option?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      options={customerGroupOptions}
                      style={{ flexGrow: 1 }}
                      suffixIcon={<PlusCircleFilled className="text-primary" onClick={handleAddCustomerGroup} />}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={12}>
                <Col span={7}>Tỉnh/thành phố</Col>
                <Col span={17}>
                  <Form.Item
                    name="province_code"
                    rules={[{ required: true, message: 'Please select a province/city!' }]}
                  >
                    <Select
                      allowClear
                      showSearch
                      placeholder="Chọn Tỉnh/thành phố"
                      optionFilterProp="children"
                      filterOption={(input, option) => {
                        const label = option?.label;
                        return typeof label === 'string' && label.toLowerCase().includes(input.toLowerCase());
                      }}
                      options={provinceOptions}
                      onChange={(value: string) => {
                        fetchDistricts(value)
                        form.setFieldsValue({ district_code: null });
                        form.setFieldsValue({ ward_code: null });
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={12}>
                <Col span={7}>Quận/Huyện</Col>
                <Col span={17}>
                  <Form.Item
                    name="district_code"
                    //rules={[{ required: true, message: 'Please select a district!' }]}
                  >
                    <Select
                      allowClear
                      showSearch
                      placeholder="Chọn Quận/Huyện"
                      optionFilterProp="children"
                      filterOption={(input, option) => {
                        const label = option?.label;
                        return typeof label === 'string' && label.toLowerCase().includes(input.toLowerCase());
                      }}
                      options={districtOptions}
                      onChange={(value: string) => {
                        fetchWards(provinceCode, value)
                        form.setFieldsValue({ ward_code: null });
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={12}>
                <Col span={7}>Phường/Xã</Col>
                <Col span={17}>
                  <Form.Item
                    name="ward_code"
                    //rules={[{ required: true, message: 'Please select a ward!' }]}
                  >
                    <Select
                      allowClear
                      showSearch
                      placeholder="Chọn Phường/Xã"
                      optionFilterProp="children"
                      filterOption={(input, option) => {
                        const label = option?.label;
                        return typeof label === 'string' && label.toLowerCase().includes(input.toLowerCase());
                      }}
                      options={wardOptions}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={12}>
                <Col span={7}>Địa chỉ</Col>
                <Col span={17}>
                  <Form.Item name="address">
                    <Input.TextArea placeholder="Số nhà, tên đường" autoSize={{ minRows: 3, maxRows: 5 }} />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        <Card className="mt-3">
          <Row gutter={12}>
            <Col span={5}>Tên công ty/Trường học</Col>
            <Col span={19}>
              <Form.Item
                name="company_name"
                //rules={[{ required: true, message: 'Please input company name!' }]}
              >
                <Input placeholder="Tên công ty/Trường học" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={5}>Chức vụ</Col>
            <Col span={19}>
              <Form.Item name="title">
                <Input placeholder="Chức vụ" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={5}>Địa chỉ công ty/Trường học</Col>
            <Col span={19}>
              <Form.Item name="company_address">
                <Input.TextArea placeholder="Địa chỉ công ty/Trường học" autoSize={{ minRows: 3, maxRows: 5 }} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <div className="text-center p-3">
          <Button type="primary" onClick={handleSubmit} loading={isUploadingAvatar} style={{ marginRight: '8px' }}>
            Cập nhật
          </Button>

          <Button type="default" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </Form>

      {showCustomerGroupForm && (
        <CustomerGroupForm
          open={showCustomerGroupForm}
          onCreate={onCreateCustomerGroup}
          onCancel={() => {
            setShowCustomerGroupForm(false);
          }}
          title="Tạo nhóm khách hàng"
        />
      )}
    </>
  );
};

export default OverviewForm;
